<script lang="ts" setup>
import InvestorHero from '@/domains/investor/components/Hero/Hero.vue'
import UiSection from '@/components/UiSection/UiSection.vue'
import TextBox from '@/domains/investor/components/TextBox/TextBox.vue'
import Table from '@/domains/investor/components/Table/Table.vue'
import Chart from '@/components/Chart/Chart.vue'

import { BsTabs } from '@afo/bsf-core'
import { useDataPresentationPage } from '@/domains/investor/composables/useDataPresentationPage'
import type { IInvestorTab, IInvestorEmits } from '@/domains/investor/types/investor.types'
import type { IAdaptChart } from '@/types/shared-types'

import { useCharts } from '@/components/Chart/Chart.composables'
import { useFetchChart } from '@/domains/investor/composables/useFetchChart'
import { computed, ref, watch } from 'vue'

import FileList from '@/domains/investor/components/FileList/FileList.vue'

definePageMeta({
  layout: 'investor',
})

const { t, locale } = useI18n()

useHead(() => ({
  title: t('pageTitles.investorDataPresentations.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.investorDataPresentations.meta'),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: t(`pageTitles.investorDataPresentations.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.investorDataPresentations.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.investorDataPresentations.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.investorDataPresentations.canonical.pl'),
    },
  ],
}))

const { updateTabContent, financialDataTabs, textBoxes, chartConfigs, files } = await useDataPresentationPage()
const { prepareChartParams } = useCharts()
const { fetchCharts } = await useFetchChart()

const chartsResponse = await fetchCharts(chartConfigs.map(config => config.id))

const charts = computed(() => {
  if (!chartsResponse) {
    return []
  }

  const { data} = chartsResponse

  return (data?.value ?? []).map((chart: IAdaptChart | null) => chart ? prepareChartParams(chart) : null)
})

const emit = defineEmits<IInvestorEmits>()
const selectedTab = ref<IInvestorTab>(financialDataTabs.value[0]!)

const changeTab = (tab: IInvestorTab) => {
  selectedTab.value = tab
  emit('update:modelValue', tab)
}

watch(selectedTab, async (newValue) => {
  await updateTabContent(newValue)
}, { immediate: true })

</script>

<template>
  <div>
    <InvestorHero
      :title="$t('investor.dataPresentations.hero.title')"
      :lead="$t('investor.dataPresentations.hero.lead')"
      image="/images/investor/hero/data-presentations.png"
      :first-button="{
        text: $t('investor.dataPresentations.hero.firstButton.label'),
        to: $t('investor.dataPresentations.hero.firstButton.to'),
      }"
    />
    
    <UiSection
      id="operational-financial"
      class="operational-financial"
    >
      <div class="section__container">
        <div class="section__row">
          <div
            v-for="(box, index) in textBoxes"
            :key="index"
            class="section__col"
            >
              <TextBox
                :box="box"
              />
          </div>
        </div>
      </div>
    </UiSection>

    <UiSection
      :id="$t('investor.dataPresentations.operationalData.sectionId')"
      class="operational-data"
    >
      <UiHeader
        :header="$t('investor.dataPresentations.operationalData.title')"
        position="center"
        :class="'section__header'"
        header-size="h5"
      />
    </UiSection>

    <template
      v-for="(chartConfig, index) in chartConfigs"
      :key="chartConfig.sectionId"
    >
      <UiSection
        v-if="charts[index]"
        :id="chartConfig.sectionId"
        class="section__chart"
        :class="chartConfig.sectionId"
      >
        <div class="section__container">
          <div class="section__row">
            <div class="section__col">
              <Chart
                :type="charts[index].type"
                :data="charts[index].data"
                :options="charts[index].options"
                :title="charts[index].title"
                variant="scrollable"
              />

              <div class="section__info">
                {{ $t('investor.dataPresentations.operationalData.chartInfo') }}
              </div>
            </div>
          </div>
        </div>
      </UiSection>
    </template>

    <UiSection
      :id="$t('investor.dataPresentations.financialData.sectionId')"
      class="financial-data"
    >
      <UiHeader
        :header="$t('investor.dataPresentations.financialData.title')"
        position="center"
        :class="'section__header'"
        header-size="h5"
      />

      <div class="section__container">
        <ClientOnly>
          <BsTabs
            :tabs="financialDataTabs"
            :model-value="selectedTab"
            :size="'large'"
            @update:model-value="changeTab($event as IInvestorTab)"
          >
            <template #financial-data-year>
              <Table
                v-if="financialDataTabs[0]?.tableData"
                :table="financialDataTabs[0].tableData"
                :options="{ highlightFirstColumn: true }"
              />
            </template>

            <template #financial-data-quarter>
              <Table
                v-if="financialDataTabs[1]?.tableData"
                :table="financialDataTabs[1].tableData"
                :options="{ highlightFirstColumn: true }"
              />
            </template>
          </BsTabs>
        </ClientOnly>
      </div>
    </UiSection>

    <FileList
      request-id="investor-prezentacje-danych-dokumenty-do-pobrania"
      :show-title="true"
      variant="list"
      :file-item-options="{
        showFormat: true,
        showSize: true,
      }"
    />

  </div>
</template>

<style lang="scss" scoped>

.section.operational-data {
  margin-bottom: 0;
  padding-bottom: 0;
}

.operational-financial {
  margin: 0;
  background-color: $gray-100;
}

.section__chart {
  padding-bottom:0;
}

.section__info {
  padding: .8rem 1.6rem;
  color:$text-400;
  text-align: center;
  background-color: $gray-100;
  border: 1px solid $gray-300;
  border-radius: .8rem;
  @include body-3;
}

.chart {
  padding-bottom:4rem;
  border-bottom: 1px solid transparent;
}

.chart-1 {
  padding-top: 0;
}

.chart-5 {
  .chart {
    border-bottom: none;
  }
}

:deep(.ui-header) {
  margin-bottom: 4.8rem;
  @include h5;
}

:deep(.bs-tabs__list) {
  margin-bottom: 2.4rem;
}

:deep(.bs-tabs__button) {
  @include body-3-medium;
  margin-right: 0.8rem;
  padding:1rem 1.6rem;
  border: 1px solid $gray-400;
  border-radius: 4.8rem;
}

:deep(.bs-tabs__button--selected) {
  border: 1px solid $text-400;
}

.files {
  padding-top:0;

  :deep(.button) {
    border-radius: 0;
  }

  &__title {
    @include h10;
    margin-bottom: 2.4rem;
  }
}

@include atS {
  :deep(.ui-header) {
    @include h5;
    margin-bottom: 6.4rem;
  }

  .chart {
    padding-bottom:6.4rem;
    border-bottom: 1px solid $base-50;
  }

  .section__info {
    display: none;
  }
}

@include atM {
  .section.operational-financial {
    margin: 0;
  }
}
</style>
